import { useIntl } from 'react-intl';
import { ScrollableStack } from '../../../modules/components/scrollableStack';
import {
  Accordion,
  AccordionDetails,
  LinearProgress,
  Paper,
  Stack,
  useTheme
} from '@mui/material';
import AccordionSummary from '@mui/material/AccordionSummary';
import { ScoreModelInput } from '../../../modules/layout/inputBlock/scoreModelInput';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';
import React, { FC, Suspense, useEffect, useRef, useState } from 'react';
import NewScoreModelButton from '../../../modules/layout/inputBlock/newScoreModelButton';
import { PaperResizer } from '../../../modules/components/paperResizer';
import useWindowDimensions from '../../../modules/layout/windowDimension';

interface DynamicInputBlockProps {
  LastComponent: FC;
}

/**
 * Input block with score model and chosen last component
 * @param props Lastcomponent and initial columnwidth
 * @constructor
 */

export const DynamicInputBlock = (props: DynamicInputBlockProps) => {
  const { LastComponent } = props;
  const intl = useIntl();
  const usePalette = useTheme().palette;
  const lastComponentRef = useRef<HTMLInputElement>(null);
  const scoreRef = useRef<HTMLInputElement>(null);
  const initialRef = useRef<HTMLInputElement>(null);
  const { height } = useWindowDimensions();

  const [lastComponentHeight, setLastComponentHeight] = useState('');

  const [isScoreModelExpanded, setIsScoreModelExpanded] = useState(true);

  const handleScoreModelAccordionChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    setIsScoreModelExpanded(isExpanded);
  };

  useEffect(() => {
    if (lastComponentRef.current) {
      const { top } = lastComponentRef.current.getBoundingClientRect();
      const componentHeight = height - top;
      setLastComponentHeight(componentHeight + 'px');
    }
  }, [isScoreModelExpanded, scoreRef?.current?.getBoundingClientRect()]);

  return (
    <PaperResizer color={usePalette.background.paper}>
      <Stack height="100vh" ref={initialRef} sx={{ overflow: 'hidden' }}>
        <Suspense fallback={<LinearProgress />}>
          <Accordion
            ref={scoreRef}
            elevation={0}
            sx={{ borderBottom: 1, borderBottomColor: usePalette.secondary.dark }}
            expanded={isScoreModelExpanded}
            onChange={handleScoreModelAccordionChange}
            defaultExpanded={true}
            disableGutters={true}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header">
              <Typography variant={'h3'}>{intl.formatMessage({ id: 'ScoreModel' })}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Paper sx={{ borderColor: 'primary' }} elevation={0} variant={'outlined'}>
                <ScrollableStack>
                  <ScoreModelInput />
                </ScrollableStack>
              </Paper>
              <NewScoreModelButton />
            </AccordionDetails>
          </Accordion>
          <div style={{ height: lastComponentHeight }} ref={lastComponentRef}>
            <LastComponent />
          </div>
        </Suspense>
      </Stack>
    </PaperResizer>
  );
};
